<template>
  <div class="pois-list-container">
    <Loader :loading="showLoader" />

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="4"
              class="px-4 px-md-6 py-4"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <div class="d-flex align-center">
                <v-btn
                  icon
                  variant="text"
                  color="white"
                  size="small"
                  @click="goBack"
                >
                  <v-icon size="20">mdi-arrow-left</v-icon>
                </v-btn>
                
                <div class="ml-3">
                  <div class="text-h6 font-weight-semibold" style="color: #ffffff;">
                    {{ getCategoryDisplayName(selectedCategory) || $t('booking.rooms') }}
                  </div>
                </div>

                <v-spacer />

                <v-btn
                  icon
                  variant="text"
                  color="white"
                  size="small"
                  @click="openMapView"
                >
                  <v-icon size="20">mdi-map</v-icon>
                </v-btn>
              </div>
            </v-card>
          </v-sheet>

          <v-sheet color="transparent" class="mt-4">
            <v-card color="white" elevation="2" rounded="xl" class="pa-4 pa-md-6">
              <v-row class="mb-4">
                <v-col cols="12">
                  <v-text-field
                    v-model="searchQuery"
                    :placeholder="$t('booking.search-poi')"
                    prepend-inner-icon="mdi-magnify"
                    variant="solo-filled"
                    density="comfortable"
                    hide-details
                    clearable
                    rounded="lg"
                    base-color="#9AA6B1"
                    color="#23A5E6"
                  />
                </v-col>
              </v-row>

              <v-card
                v-if="filteredPois.length === 0 && !showLoader"
                variant="outlined"
                rounded="xl"
                class="pa-12 text-center"
              >
                <v-icon size="96" color="#B7BFC7">mdi-door</v-icon>
                <h3 class="text-h6 mt-4 font-weight-semibold" style="color: #373F47;">
                  No rooms found
                </h3>
                <p class="text-body-2 mt-2" style="color: #9AA6B1;">
                  Try adjusting your search criteria
                </p>
              </v-card>

              <v-row v-else dense>
                <v-col
                  v-for="(poi) in filteredPois"
                  :key="poi.id"
                  cols="12"
                  sm="6"
                  lg="4"
                  xl="3"
                >
                  <v-card
                    elevation="2"
                    class="poi-card"
                    rounded="xl"
                    @click="goToPoi(poi)"
                    hover
                  >
                    <div class="card-image-section">
                      <img
                        v-if="poi.image"
                        :src="poi.image"
                        alt="Room"
                        class="poi-image"
                      />
                      <div v-else class="fallback-image">
                        <img :src="fallbackImage" alt="Logo" class="fallback-logo" />
                      </div>
                      <div class="image-overlay"></div>
                    </div>

                    <div class="card-content pa-4">
                      <h3 class="poi-name text-subtitle-1 font-weight-semibold mb-3">
                        {{ poi.name }}
                      </h3>
                      
                      <div class="poi-details">
                        <div class="detail-row">
                          <v-icon size="16" class="detail-icon">mdi-account-group</v-icon>
                          <span class="detail-label">{{ $t('booking.capacity') }}:</span>
                          <span class="detail-value">{{ poi.capacity }}</span>
                        </div>
                        
                        <div class="detail-row">
                          <v-icon size="16" class="detail-icon">mdi-tag</v-icon>
                          <span class="detail-label">{{ $t('booking.category') }}:</span>
                          <span class="detail-value">{{ getCategoryDisplayName(poi.category) }}</span>
                        </div>
                        
                        <div class="detail-row">
                          <v-icon size="16" class="detail-icon">mdi-floor-plan</v-icon>
                          <span class="detail-label">{{ $t('booking.floor') }}:</span>
                          <span class="detail-value">#{{ poi.floor.number }}</span>
                        </div>
                        
                        <div class="detail-row">
                          <v-icon size="16" class="detail-icon">mdi-clock-outline</v-icon>
                          <span class="detail-label">{{ $t('booking.availability') }}:</span>
                          <span class="detail-value">{{ poi.availableFrom ? poi.availableFrom + ' - ' + poi.availableUntil : '06:00 - 22:00' }}</span>
                        </div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <MapView ref="mapView" />
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getToolbarLogoImage, getToolBarCustomerName } from '@/assets/Branding/branding.js'
import { useRouter, useRoute } from 'vue-router'
import { getPoiCategoryById, getBuildings, getPoiById, getFloorById } from '@/controllers/BaseController.js'
import { getCookie } from '@/services/utils'
import MapView from '@/components/general/MapView.vue'
import { getHardwareByType } from '@/controllers/BackboneAPI'
import moment from 'moment-timezone'
import Loader from '@/components/general/Loader.vue'
import lodash from 'lodash'

export default defineComponent({
  name: 'PoisListView',
  components: {
    Loader,
    MapView,
  },
  setup() {
    const { locale } = useI18n()
    const showLoader = ref(false)
    const logo = ref('')
    const customerName = ref('')
    const router = useRouter()
    const route = useRoute()
    const pois = ref([])
    const selectedCategory = ref(null)
    const selectedBuilding = ref(null)
    const fallbackImage = ref('')
    const mapView = ref(null)
    const searchQuery = ref('')
    const currentLanguage = ref('en')

    const backupImageMap = {
      'meeting room': require('@/assets/PoiImages/backup_poi_images/meeting_room.png'),
      'lab': require('@/assets/PoiImages/backup_poi_images/lab_room.png'),
      'restrooms': require('@/assets/PoiImages/backup_poi_images/rest_room.png'),
      'printer': require('@/assets/PoiImages/backup_poi_images/printer_room.png'),
      'studio': require('@/assets/PoiImages/backup_poi_images/studio.png'),
      'lager': require('@/assets/PoiImages/backup_poi_images/lager_room.png'),
      'food and beverages': require('@/assets/PoiImages/backup_poi_images/kitchen_room.png'),
      'event space': require('@/assets/PoiImages/backup_poi_images/event_space.png'),
      'showers': require('@/assets/PoiImages/backup_poi_images/shower.png'),
    }

    const getCategoryDisplayName = (category) => {
      if (!category) return ''
      if (currentLanguage.value === 'de' && category.name_de) {
        return category.name_de
      }
      return category.name
    }

    const getCategoryImage = (categoryName) => {
      const normalizedName = categoryName.toLowerCase().trim()
      
      if (backupImageMap[normalizedName]) {
        return backupImageMap[normalizedName]
      }
      
      for (const [key, image] of Object.entries(backupImageMap)) {
        if (normalizedName.includes(key) || key.includes(normalizedName)) {
          return image
        }
      }
      
      return null
    }

    const loadLanguageFromCookie = async () => {
      const savedLanguage = await getCookie('language')
      if (savedLanguage) {
        currentLanguage.value = savedLanguage
        locale.value = savedLanguage
      }
    }

    const goBack = () => {
      window.history.back()
    }

    function openMapView() {
      let SELECTED_BUILDING = selectedBuilding.value
      SELECTED_BUILDING = JSON.parse(JSON.stringify(SELECTED_BUILDING))
      let POI_CATEGORIES = [JSON.parse(JSON.stringify(selectedCategory.value))]
      let POIS = JSON.parse(JSON.stringify(pois.value))
      POIS = lodash.flatten(POIS)
      mapView.value.openMap(SELECTED_BUILDING, POIS, POI_CATEGORIES)
    }

    const goToPoi = poi => {
      let categoryId = selectedCategory.value.id
      router.push({ name: 'PoiDetails', params: { poi_id: poi.id, category_id: categoryId } })
    }

    const filteredPois = computed(() => {
      if (!searchQuery.value) {
        return pois.value
      }
      return pois.value.filter(poi =>
        poi.name.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    })

    onMounted(async () => {
      try {
        showLoader.value = true

        await loadLanguageFromCookie()

        const [logoImage, customerNameValue] = await Promise.all([getToolbarLogoImage(), getToolBarCustomerName()])

        logo.value = logoImage
        customerName.value = customerNameValue

        const canvas = document.createElement('canvas')
        canvas.width = 200
        canvas.height = 200
        const ctx = canvas.getContext('2d')

        ctx.fillStyle = 'black'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        const image = new Image()
        image.src = logoImage
        image.onload = () => {
          const imgWidth = 270
          const imgHeight = 200
          const x = (canvas.width - imgWidth) / 2
          const y = (canvas.height - imgHeight) / 2
          ctx.drawImage(image, x, y, imgWidth, imgHeight)
          fallbackImage.value = canvas.toDataURL()
        }

        const categoryId = route.params.category_id
        const buildingId = route.params.building_id

        const hardwareTypes = ['BLINDS', 'VENTILATOR', 'LIGHT', 'HEATER']
        const hardwarePromises = hardwareTypes.map(type => getHardwareByType(buildingId, type).catch(() => []))
        const hardwareArrays = await Promise.all(hardwarePromises)
        const hardwareData = Object.fromEntries(hardwareTypes.map((type, index) => [type, hardwareArrays[index]]))

        const [buildings, category] = await Promise.all([getBuildings(), getPoiCategoryById(categoryId)])

        selectedBuilding.value = buildings.find(b => b.id === buildingId)
        selectedCategory.value = category

        const categoryImage = getCategoryImage(category.name)

        const poiIds = category?.pois?.ids || []
        const selectedPoisPromises = poiIds.map(async poiId => {
          const poi = await getPoiById(poiId)
          const floor = await getFloorById(poi?.floors?.ids[0])

          let image = categoryImage
          let logo = null
          
          if (poi.availableFrom && poi.availableUntil) {
            poi.availableFrom = moment.tz(poi.availableFrom, 'HH:mm', 'Europe/Paris').utc().format('HH:mm')
            poi.availableUntil = moment.tz(poi.availableUntil, 'HH:mm', 'Europe/Paris').utc().format('HH:mm')
          }

          if (poi?.configs['room-control'] !== undefined && poi?.configs['room-control'] === 'true') {
            const hardwares = hardwareTypes.flatMap(type =>
              hardwareData[type].filter(hardware => hardware.configs['poi-id'] === poi.id).map(hardware => ({ type, id: hardware.id }))
            )
            poi.hardwares = hardwares
          }

          return { ...poi, floor, image, logo, category }
        })

        let selectedPois = await Promise.all(selectedPoisPromises)
        selectedPois = lodash.orderBy(
          selectedPois,
          [
            poi => {
              const name = lodash.get(poi, 'name', '')
              const isNumeric = /^\d/.test(name)
              return (isNumeric ? '1' : '2') + name
            },
          ],
          ['asc']
        )
        console.log(selectedPois)
        pois.value = selectedPois
      } catch (error) {
        console.error(error)
      } finally {
        showLoader.value = false
      }
    })

    return {
      showLoader,
      openMapView,
      customerName,
      router,
      route,
      logo,
      pois,
      selectedCategory,
      selectedBuilding,
      fallbackImage,
      mapView,
      moment,
      goToPoi,
      goBack,
      searchQuery,
      filteredPois,
      getCategoryDisplayName,
      currentLanguage
    }
  },
})
</script>

<style scoped>
.pois-list-container {
  min-height: var(--app-viewport-height, 100vh);
  background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);
}

.v-card[style*="linear-gradient(135deg, #285096"]::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 1;
  pointer-events: none;
  border-radius: inherit;
}

.poi-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  overflow: hidden;
}

.poi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(40, 80, 150, 0.15) !important;
  border-color: rgba(40, 80, 150, 0.2);
}

.card-image-section {
  height: 180px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(35, 165, 230, 0.1) 0%, rgba(40, 80, 150, 0.1) 100%);
  position: relative;
}

.poi-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.poi-card:hover .poi-image {
  transform: scale(1.1);
}

.fallback-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000000;
}

.fallback-logo {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.3) 100%);
}

.poi-name {
  color: #001437;
  line-height: 1.3;
  min-height: 48px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.poi-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4E5965;
}

.detail-icon {
  color: #23A5E6;
  flex-shrink: 0;
}

.detail-label {
  color: #4E5965;
  font-weight: 500;
}

.detail-value {
  color: #285096;
  font-weight: 600;
  margin-left: auto;
}

.poi-card {
  animation: fadeInUp 0.4s ease-out backwards;
}

.poi-card:nth-child(1) { animation-delay: 0.05s; }
.poi-card:nth-child(2) { animation-delay: 0.1s; }
.poi-card:nth-child(3) { animation-delay: 0.15s; }
.poi-card:nth-child(4) { animation-delay: 0.2s; }
.poi-card:nth-child(5) { animation-delay: 0.25s; }
.poi-card:nth-child(6) { animation-delay: 0.3s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .card-image-section {
    height: 160px;
  }
  
  .poi-name {
    font-size: 0.95rem;
    min-height: 44px;
  }
  
  .detail-row {
    font-size: 12px;
  }
}

@media (max-width: 600px) {
  .card-content {
    padding: 12px !important;
  }
}

@media (max-width: 400px) {
  .card-image-section {
    height: 140px;
  }
}
</style>
